import type { ComponentChildren } from "preact";
import { goBack } from "../../state/route";
import { cx } from "../cx";
import { Visualizer } from "./Visualizer";

export interface ScreenFrameProps {
  title: string;
  /** Small uppercase line above the title. */
  kicker?: string;
  /** Defaults to popping the screen stack. Screens with unsaved changes pass their own guard. */
  onBack?: () => void;
  backLabel?: string;
  /** Right side of the header. */
  actions?: ComponentChildren;
  footer?: ComponentChildren;
  children: ComponentChildren;
  class?: string;
  bodyClass?: string;
}

/**
 * Shared layout of every menu screen: a chrome chassis with a glass title plate, a scrolling glass
 * body well and an optional chrome footer. The size does not depend on its content, so nothing
 * shifts while data loads.
 */
export function ScreenFrame({
  title,
  kicker,
  onBack,
  backLabel = "Back",
  actions,
  footer,
  children,
  class: cls,
  bodyClass,
}: ScreenFrameProps) {
  return (
    <section class={cx("frame", cls)} aria-labelledby="frame-title">
      <header class="frame__head">
        <button type="button" class="frame__back" onClick={() => (onBack ?? goBack)()} data-nav="">
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <path d="M10 2 4 8l6 6" stroke="currentColor" stroke-width="2" fill="none" />
          </svg>
          <span>{backLabel}</span>
          <kbd class="frame__kbd">Esc</kbd>
        </button>
        <div class="frame__titles">
          {kicker && <span class="frame__kicker">{kicker}</span>}
          <h1 id="frame-title" class="frame__title">
            {title}
          </h1>
          <Visualizer class="frame__viz" bars={12} />
        </div>
        <div class="frame__actions">{actions}</div>
      </header>
      <div class={cx("frame__body", bodyClass)}>{children}</div>
      {footer && <footer class="frame__foot">{footer}</footer>}
    </section>
  );
}
